import { useMemo } from 'react'
import {
  useCollectionData,
  useDocumentDataOnce,
} from 'react-firebase-hooks/firestore'
import {
  Profile,
  badgeConverter,
  badgesRef,
  bookConverter,
  bookRef,
  categoryConverter,
  macroConverter,
  pageConverter,
  profileConverter,
  profileRef,
  referenceConverter,
  tagConverter,
} from 'web_core/firebase'
import { useMacros, useQueryData, useReferences, useTags } from 'web_core/hooks'
import { useCategory } from 'web_core/hooks/models/useCategory'
import { usePages } from 'web_core/hooks/models/usePages'
import { deserialize, deserializeArray, isBlank } from 'web_core/utils'
import { BookPageProps } from '~/static_props/book'

export const useBookPageProps = ({
  book: staticBook,
  pages: staticPages,
  currentPageId: staticCurrentPageId,
  profile: staticProfile,
  category: staticCategory,
  tags: staticTags,
  references: staticReferences,
  macros: staticMacros,
  badges: staticBadges,
  indexable,
}: BookPageProps) => {
  const id = useQueryData()
  const [book, loadingBook, bookError] = useDocumentDataOnce(
    !isBlank(id) && id !== '' ? bookRef(id) : null,
    {
      initialValue: staticBook
        ? deserialize(staticBook, { ref: bookConverter })
        : undefined,
    }
  )

  const { pages, loading: loadingPages } = usePages(
    book,
    staticPages
      ? deserializeArray(staticPages, { ref: pageConverter })
      : undefined
  )
  const currentPageId = useQueryData('page_id') ?? staticCurrentPageId
  const currentPage = useMemo(() => {
    return pages?.find((p) => p.id === currentPageId)
  }, [pages, currentPageId])
  const [profile, loadingProfile] = useDocumentDataOnce<Profile>(
    book ? profileRef(book.created_by) : null,
    {
      initialValue: staticProfile
        ? deserialize(staticProfile, {
            ref: profileConverter,
          })
        : undefined,
    }
  )
  const { category, loading: loadingCategory } = useCategory(
    book?.category_id,
    staticCategory
      ? deserialize(staticCategory, { ref: categoryConverter })
      : undefined
  )
  const { tags, loading: loadingTags } = useTags(
    book?.tag_ids,
    staticTags ? deserializeArray(staticTags, { ref: tagConverter }) : undefined
  )
  const { references, loading: loadingReferences } = useReferences(
    book ? book.ref : undefined,
    true,
    staticReferences
      ? deserializeArray(staticReferences, { ref: referenceConverter })
      : undefined
  )
  const { macros, loading: loadingMacros } = useMacros(
    book ? book.ref : undefined,
    true,
    staticMacros
      ? deserializeArray(staticMacros, { ref: macroConverter })
      : undefined
  )

  const [badges, loadingBadges] = useCollectionData(
    book ? badgesRef(book.ref) : null,
    {
      initialValue: staticBadges
        ? deserializeArray(staticBadges, { ref: badgeConverter })
        : undefined,
    }
  )

  const isLoading =
    isBlank(id) ||
    id === '' ||
    loadingBook ||
    loadingPages ||
    loadingProfile ||
    loadingCategory ||
    loadingTags ||
    loadingReferences ||
    loadingMacros ||
    loadingBadges

  const isNotFound =
    bookError !== undefined ||
    (!isLoading &&
      (!book ||
        !pages ||
        !profile ||
        !category ||
        !tags ||
        !references ||
        !macros ||
        (currentPageId !== null && !currentPage) ||
        !badges))

  return {
    isNotFound,
    book,
    pages,
    currentPage,
    profile,
    category,
    tags,
    macros,
    references,
    badges,
    indexable,
    isLoading,
  }
}
